// src/services/frontend-bundler/encryption.service.ts

export class EncryptionService {
  private readonly SALT_LENGTH = 16;
  private readonly IV_LENGTH = 12;
  private readonly TAG_LENGTH = 16;
  private readonly ITERATIONS = 100000;
  private readonly KEY_LENGTH = 256;

  validatePassword(password: string): boolean {
  // Change minimum from 12 to 4 characters
  if (password.length < 4) return false;
  
  // Keep other requirements or remove them if you want
  // You might want to make these optional for a 4-char password
  if (!/[A-Z]/.test(password)) return false;
  if (!/[a-z]/.test(password)) return false;
  if (!/[0-9]/.test(password)) return false;
  
  return true;
}
  /**
   * Derive encryption key from password
   */
  private async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const passwordBuffer = encoder.encode(password);
    
    // Import password as key material
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );
    
    // Derive AES-GCM key
    return await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: this.ITERATIONS,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: this.KEY_LENGTH },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypt data with password
   */
  async encrypt(data: string, password: string): Promise<string> {
    try {
      // Generate random salt and IV
      const salt = crypto.getRandomValues(new Uint8Array(this.SALT_LENGTH));
      const iv = crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));
      
      // Derive key from password
      const key = await this.deriveKey(password, salt);
      
      // Encode data
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);
      
      // Encrypt data
      const encryptedBuffer = await crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: iv
        },
        key,
        dataBuffer
      );
      
      // Combine salt, iv, and encrypted data
      const encryptedArray = new Uint8Array(encryptedBuffer);
      const combined = new Uint8Array(
        salt.length + iv.length + encryptedArray.length
      );
      
      combined.set(salt, 0);
      combined.set(iv, salt.length);
      combined.set(encryptedArray, salt.length + iv.length);
      
      // Convert to base64 for storage
      return this.arrayBufferToBase64(combined);
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypt data with password
   */
  async decrypt(encryptedData: string, password: string): Promise<string> {
    try {
      // Convert from base64
      const combined = this.base64ToArrayBuffer(encryptedData);
      const combinedArray = new Uint8Array(combined);
      
      // Extract salt, iv, and encrypted data
      const salt = combinedArray.slice(0, this.SALT_LENGTH);
      const iv = combinedArray.slice(this.SALT_LENGTH, this.SALT_LENGTH + this.IV_LENGTH);
      const encrypted = combinedArray.slice(this.SALT_LENGTH + this.IV_LENGTH);
      
      // Derive key from password
      const key = await this.deriveKey(password, salt);
      
      // Decrypt data
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv
        },
        key,
        encrypted
      );
      
      // Decode data
      const decoder = new TextDecoder();
      return decoder.decode(decryptedBuffer);
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt data - invalid password or corrupted data');
    }
  }

  /**
   * Hash data for verification (non-reversible)
   */
  async hash(data: string): Promise<string> {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    return this.arrayBufferToHex(hashBuffer);
  }

  /**
   * Generate secure random password
   */
  generateSecurePassword(length: number = 16): string {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const all = uppercase + lowercase + numbers + symbols;
    
    let password = '';
    
    // Ensure at least one of each required character type
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the rest randomly
    const array = new Uint8Array(length - 4);
    crypto.getRandomValues(array);
    
    for (let i = 0; i < array.length; i++) {
      password += all[array[i] % all.length];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  }

  /**
   * Create encrypted backup of sensitive data
   */
  async createBackup(data: any, password: string): Promise<{
    backup: string;
    checksum: string;
    timestamp: string;
  }> {
    const jsonData = JSON.stringify(data);
    const encrypted = await this.encrypt(jsonData, password);
    const checksum = await this.hash(encrypted);
    
    return {
      backup: encrypted,
      checksum,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Restore from encrypted backup
   */
  async restoreBackup(backup: string, checksum: string, password: string): Promise<any> {
    // Verify checksum
    const currentChecksum = await this.hash(backup);
    if (currentChecksum !== checksum) {
      throw new Error('Backup integrity check failed - data may be corrupted');
    }
    
    // Decrypt backup
    const decrypted = await this.decrypt(backup, password);
    return JSON.parse(decrypted);
  }

  /**
   * Helper functions
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private arrayBufferToHex(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    return Array.from(bytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Password strength meter
   */
  getPasswordStrength(password: string): {
  score: number;
  strength: 'weak' | 'fair' | 'good' | 'strong' | 'very-strong';
  feedback: string[];
} {
  let score = 0;
  const feedback: string[] = [];
  
  // Update length scoring
  if (password.length >= 4) score += 1;  // Changed from 8
  if (password.length >= 8) score += 1;   // Changed from 12
  if (password.length >= 12) score += 1;  // Changed from 16
  else if (password.length < 4) feedback.push('Use at least 4 characters'); // Changed from 12
    
    // Character variety
    if (/[a-z]/.test(password)) score += 1;
    else feedback.push('Add lowercase letters');
    
    if (/[A-Z]/.test(password)) score += 1;
    else feedback.push('Add uppercase letters');
    
    if (/[0-9]/.test(password)) score += 1;
    else feedback.push('Add numbers');
    
    if (/[^a-zA-Z0-9]/.test(password)) score += 1;
    else feedback.push('Add special characters');
    
    // Common patterns (negative score)
    if (/(.)\1{2,}/.test(password)) {
      score -= 1;
      feedback.push('Avoid repeated characters');
    }
    
    if (/^[a-zA-Z]+$/.test(password) || /^[0-9]+$/.test(password)) {
      score -= 1;
      feedback.push('Mix different character types');
    }
    
    // Determine strength
    let strength: 'weak' | 'fair' | 'good' | 'strong' | 'very-strong';
    if (score < 3) strength = 'weak';
    else if (score < 5) strength = 'fair';
    else if (score < 7) strength = 'good';
    else if (score < 9) strength = 'strong';
    else strength = 'very-strong';
    
    return { score: Math.max(0, Math.min(10, score)), strength, feedback };
  }
}

export const encryptionService = new EncryptionService();